import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSession } from '../context/SessionContext';
import { createSession } from '../utils/api';
import api from '../utils/api';
import { testServerConnection } from '../utils/testConnection';
import { storeSessionObject, storeParticipantForSession } from '../utils/sessionStorage';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useSearchParams } from 'react-router-dom';

const CreateSession = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionContext = useSession();
  const { user, token } = useAuth();
  const {
    organizations,
    teams,
    currentOrganization,
    currentTeam,
    workspaceMode,
    refreshTeams,
  } = useWorkspace();
  
  // Debug logging to identify the issue
  console.log('SessionContext values in CreateSession:', sessionContext);
  console.log('setParticipant type:', typeof sessionContext.setParticipant);
  
  const { 
    setSession, 
    setParticipant, 
    clearSession, 
    isConnected, 
    error: contextError, 
    reconnectSocket,
    updateParticipant
  } = sessionContext;
  
  // Additional debug check after destructuring
  console.log('After destructuring - setParticipant:', typeof setParticipant);
  console.log('After destructuring - updateParticipant:', typeof updateParticipant);
  
  // Get teamId from URL params if provided
  const urlTeamId = searchParams.get('teamId');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cardDeckType: 'fibonacci',
    estimationMode: 'realtime',
    asyncDeadlineType: '24h',
    asyncDeadline: null,
    autoRevealOnComplete: true,
    organizationId: currentOrganization?.id || '',
    teamId: urlTeamId || currentTeam?.id || '',
  });

  // Update form when workspace changes
  useEffect(() => {
    if (urlTeamId) {
      // If teamId is in URL, find the org
      const teamOrg = organizations.find(org =>
        org.teams?.some(t => t.id === urlTeamId)
      );
      if (teamOrg) {
        setFormData(fd => ({
          ...fd,
          organizationId: teamOrg.id,
          teamId: urlTeamId,
        }));
        refreshTeams(teamOrg.id);
      }
    } else if (workspaceMode === 'team' && currentTeam) {
      setFormData(fd => ({
        ...fd,
        organizationId: currentOrganization?.id || '',
        teamId: currentTeam.id,
      }));
    } else if (workspaceMode === 'organization' && currentOrganization) {
      setFormData(fd => ({
        ...fd,
        organizationId: currentOrganization.id,
        teamId: '',
      }));
    }
  }, [workspaceMode, currentOrganization, currentTeam, urlTeamId, organizations, refreshTeams]);

  // Auto-select organization if user has exactly one
  useEffect(() => {
    if (
      workspaceMode === 'personal' &&
      organizations.length === 1 &&
      !formData.organizationId
    ) {
      const singleOrg = organizations[0];
      setFormData(fd => ({
        ...fd,
        organizationId: singleOrg.id,
        teamId: '',
      }));
      refreshTeams(singleOrg.id);
    }
  }, [organizations, workspaceMode, formData.organizationId, refreshTeams]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serverStatus, setServerStatus] = useState({ checking: true, isOnline: false });
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [connectionRetries, setConnectionRetries] = useState(0);
  
  // Pre-fill name if user is logged in (optional UX improvement)
  useEffect(() => {
    if (user && user.name) {
      // setFormData(fd => ({ ...fd, createdBy: user.name })); // No longer needed
    }
  }, [user]);
  
  // Check server status on component mount
  useEffect(() => {
    const checkServer = async () => {
      setServerStatus({ checking: true, isOnline: false });
      try {
        const status = await api.checkServerStatus();
        console.log('Server status check result:', status);
        setServerStatus({ checking: false, isOnline: status.isOnline });
        
        if (!status.isOnline) {
          setError('Server is not available. Please ensure the server is running.');
        } else {
          setError(null);
        }
      } catch (err) {
        console.error('Error checking server status:', err);
        setServerStatus({ checking: false, isOnline: false });
        setError('Failed to check server status. Please ensure the server is running.');
      }
    };
    
    checkServer();
  }, []);
  
  // Check server connection status from context
  useEffect(() => {
    if (contextError && contextError.includes('Socket not available')) {
      setError('Server connection error: Please ensure the server is running.');
      setServerStatus(prev => ({ ...prev, isOnline: false }));
      
      // Auto-retry connection if error persists
      if (connectionRetries < 3) {
        console.log(`Auto-retrying connection (attempt ${connectionRetries + 1}/3)...`);
        const timer = setTimeout(() => {
          reconnectSocket();
          setConnectionRetries(prev => prev + 1);
        }, 3000);
        
        return () => clearTimeout(timer);
      }
    } else if (isConnected) {
      // Reset retries when connected
      setConnectionRetries(0);
    }
  }, [contextError, isConnected, connectionRetries, reconnectSocket]);
  
  const { name, description, cardDeckType, estimationMode, asyncDeadlineType, asyncDeadline, autoRevealOnComplete, organizationId, teamId } = formData;

  const handleOrgChange = async (e) => {
    const newOrgId = e.target.value;
    setFormData(fd => ({
      ...fd,
      organizationId: newOrgId,
      teamId: '', // Reset team when org changes
    }));
    if (newOrgId) {
      await refreshTeams(newOrgId);
    }
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleDateChange = (newDate) => {
    setFormData({ ...formData, asyncDeadline: newDate });
  };

  const handleModeToggle = (e) => {
    setFormData({
      ...formData,
      estimationMode: e.target.checked ? 'async' : 'realtime'
    });
  };
  
  const handleTestConnection = async () => {
    setTestingConnection(true);
    setTestResult(null);
    
    try {
      console.log('Running server connection test...');
      
      // Get server URL
      const serverUrl = process.env.NODE_ENV === 'production' 
        ? window.location.origin
        : 'http://localhost:5000';
        
      console.log('Testing connection to server:', serverUrl);
      
      // Try direct health check first with fetch
      try {
        const response = await fetch(`${serverUrl}/health`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });
        
        if (response.ok) {
          console.log('Health check passed!', await response.json());
          setTestResult({
            success: true,
            message: 'Connection test successful! Server is reachable.'
          });
          
          // If test is successful but socket is not connected, try to reconnect
          if (!isConnected) {
            console.log('Test successful but socket not connected - attempting reconnect');
            reconnectSocket();
          }
          
          setTestingConnection(false);
          return;
        }
        
        console.log('Health check failed with status:', response.status);
      } catch (healthError) {
        console.error('Health check failed:', healthError);
      }
      
      // Fall back to full connection test
      const result = await testServerConnection();
      
      setTestResult({
        success: result,
        message: result 
          ? 'Connection test successful! Server is reachable.' 
          : 'Connection test failed. The server may not be running or may be inaccessible.'
      });
      
      // If test is successful but socket is not connected, try to reconnect
      if (result && !isConnected) {
        console.log('Test successful but socket not connected - attempting reconnect');
        reconnectSocket();
      }
    } catch (err) {
      console.error('Test connection error:', err);
      setTestResult({
        success: false,
        message: `Connection test error: ${err.message}`
      });
    } finally {
      setTestingConnection(false);
    }
  };
  
  const handleRetryConnection = async () => {
    setError(null);
    setServerStatus({ checking: true, isOnline: false });
    
    try {
      // First try to reconnect the socket
      reconnectSocket();
      
      // Then check server status
      const status = await api.checkServerStatus();
      setServerStatus({ checking: false, isOnline: status.isOnline });
      
      if (!status.isOnline) {
        setError('Server is still not available. Please ensure the server is running.');
      }
    } catch (err) {
      console.error('Error checking server status:', err);
      setServerStatus({ checking: false, isOnline: false });
      setError('Failed to check server status. Please ensure the server is running.');
    }
  };
  
  const onSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Session name is required');
      return;
    }
    
    if (!token) {
      setError('You must be logged in to create a session. Please log in and try again.');
      setLoading(false);
      console.error('Create session attempt without auth token.');
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      console.log('Attempting to create session with data:', { name, cardDeckType });
      console.log('Current auth token being used (from AuthContext):', token);

      // Clear any existing session data before creating a new session
      console.log('Clearing previous session data before creating new session');
      clearSession();

      const sessionCreationPayload = {
        name,
        cardDeckType,
        estimationMode,
        ...(description && { description }),
        ...(estimationMode === 'async' && {
          asyncDeadlineType,
          asyncDeadline: asyncDeadlineType === 'custom' ? asyncDeadline : null,
          autoRevealOnComplete
        }),
        ...(organizationId && { organizationId }),
        ...(teamId && { teamId }),
      };

      // Use the createSession utility from api.js which uses the configured axios instance
      const newSessionData = await createSession(sessionCreationPayload);

      console.log('Session created successfully:', newSessionData);

      // The backend now creates the facilitator participant.
      // We just need to store the session and participant (facilitator) info from the response.
      setSession(newSessionData.session);
      setParticipant(newSessionData.facilitator); 
      
      // Store in localStorage using new functions
      storeSessionObject(newSessionData.session);
      storeParticipantForSession(newSessionData.session.sessionId, newSessionData.facilitator);

      // Navigate to the new session page
      navigate(`/meeting/${newSessionData.session.sessionId}?pid=${newSessionData.facilitator.id}`);

    } catch (err) {
      console.error('Error creating session:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || err.message || 'An unknown error occurred.';
      const errorDetails = err.response?.data ? JSON.stringify(err.response.data) : 'No additional details.';
      setError(`Error creating session: ${errorMessage}`);
      console.error('Error details:', errorDetails);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={8} lg={6}>
        <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Create New Session
          </Typography>
          
          {serverStatus.checking ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>Checking server status...</Typography>
            </Box>
          ) : (
            <Box component="form" onSubmit={onSubmit} noValidate sx={{ mt: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    name="name"
                    label="Session Name"
                    fullWidth
                    value={name}
                    onChange={onChange}
                    required
                    autoFocus
                    error={error && !name.trim()}
                    helperText={error && !name.trim() ? 'Session name is required' : ''}
                    disabled={loading || !serverStatus.isOnline}
                    placeholder="e.g., Sprint 42 Planning"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    name="description"
                    label="Session Description (Optional)"
                    fullWidth
                    multiline
                    rows={3}
                    value={description}
                    onChange={onChange}
                    disabled={loading || !serverStatus.isOnline}
                    placeholder="Add context for your team, e.g., Loom video links, meeting notes..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="card-deck-label">Card Deck</InputLabel>
                    <Select
                      labelId="card-deck-label"
                      name="cardDeckType"
                      value={cardDeckType}
                      onChange={onChange}
                      label="Card Deck"
                      disabled={loading || !serverStatus.isOnline}
                    >
                      <MenuItem value="fibonacci">Fibonacci (0, 1, 2, 3, 5, 8, 13, ...)</MenuItem>
                      <MenuItem value="modified-fibonacci">Modified Fibonacci (0, ½, 1, 2, 3, 5, ...)</MenuItem>
                      <MenuItem value="t-shirt">T-Shirt Sizes (XS, S, M, L, XL, XXL)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Organization & Team Selection */}
                {organizations.length > 0 && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="organization-label">Organization (Optional)</InputLabel>
                        <Select
                          labelId="organization-label"
                          name="organizationId"
                          value={organizationId}
                          onChange={handleOrgChange}
                          label="Organization (Optional)"
                          disabled={loading || !serverStatus.isOnline}
                        >
                          <MenuItem value="">
                            <em>Personal (No Organization)</em>
                          </MenuItem>
                          {organizations.map((org) => (
                            <MenuItem key={org.id} value={org.id}>
                              {org.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={!organizationId}>
                        <InputLabel id="team-label">Team (Optional)</InputLabel>
                        <Select
                          labelId="team-label"
                          name="teamId"
                          value={teamId}
                          onChange={onChange}
                          label="Team (Optional)"
                          disabled={loading || !serverStatus.isOnline || !organizationId}
                        >
                          <MenuItem value="">
                            <em>No Team</em>
                          </MenuItem>
                          {teams.map((team) => (
                            <MenuItem key={team.id} value={team.id}>
                              {team.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 2, bgcolor: 'background.default' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={estimationMode === 'async'}
                          onChange={handleModeToggle}
                          name="estimationMode"
                          color="primary"
                          disabled={loading || !serverStatus.isOnline}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            Asynchronous Estimation Mode
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Allow participants to vote on all items at their own pace, with a deadline for completion.
                          </Typography>
                        </Box>
                      }
                    />

                    <Collapse in={estimationMode === 'async'}>
                      <Box sx={{ mt: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel id="deadline-type-label">Voting Deadline</InputLabel>
                              <Select
                                labelId="deadline-type-label"
                                name="asyncDeadlineType"
                                value={asyncDeadlineType}
                                onChange={onChange}
                                label="Voting Deadline"
                                disabled={loading || !serverStatus.isOnline}
                              >
                                <MenuItem value="24h">24 Hours</MenuItem>
                                <MenuItem value="48h">48 Hours</MenuItem>
                                <MenuItem value="1week">1 Week</MenuItem>
                                <MenuItem value="custom">Custom Date/Time</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {asyncDeadlineType === 'custom' && (
                            <Grid item xs={12} sm={6}>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DateTimePicker
                                  label="Custom Deadline"
                                  value={asyncDeadline}
                                  onChange={handleDateChange}
                                  minDateTime={new Date()}
                                  disabled={loading || !serverStatus.isOnline}
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      fullWidth: true
                                    }
                                  }}
                                />
                              </LocalizationProvider>
                            </Grid>
                          )}

                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={autoRevealOnComplete}
                                  onChange={onChange}
                                  name="autoRevealOnComplete"
                                  color="primary"
                                  disabled={loading || !serverStatus.isOnline}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">Auto-reveal when all participants vote</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Automatically reveal votes for an item when everyone has voted
                                  </Typography>
                                </Box>
                              }
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </Paper>
                </Grid>

                {error && (
                  <Grid item xs={12}>
                    <Alert severity="error">{error}</Alert>
                    {error.includes('server') && (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleRetryConnection}
                        sx={{ mt: 2 }}
                      >
                        Retry Connection
                      </Button>
                    )}
                  </Grid>
                )}
                
                {!serverStatus.isOnline && !error && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      Server connection is not available. Please ensure the server is running.
                    </Alert>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={handleRetryConnection}
                      sx={{ mt: 2 }}
                    >
                      Retry Connection
                    </Button>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="info"
                    fullWidth
                    onClick={handleTestConnection}
                    disabled={testingConnection}
                    sx={{ mb: 2 }}
                  >
                    {testingConnection ? 'Testing Connection...' : 'Test Server Connection'}
                  </Button>
                  
                  {testResult && (
                    <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                      {testResult.message}
                    </Alert>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={loading || !serverStatus.isOnline}
                    sx={{ mt: 2 }}
                  >
                    {loading ? 'Creating...' : 'Create Session'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default CreateSession; 
